import type { PriceBreakdown, PricingConfig } from '@printtat/shared';
import { apiFetch, apiJson } from '@/lib/apiFetch';

export interface DesignListItem {
  id: string;
  name: string;
  description: string | null;
  previewUrl: string | null;
  status: string;
  productId: string;
  productName: string | null;
  productSlug: string | null;
  primaryImage: string | null;
  totalPrice: number | null;
  surfacesUsed: string[];
  productSurfaces: string[];
  priceBreakdown: PriceBreakdown | null;
  surfaceExports: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

export interface DesignDetailProduct {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  basePrice: number;
  pricingConfig: PricingConfig | null;
  printAreas: {
    surfaces: string[];
    dimensions: Record<string, { width: number; height: number }>;
    canvasOffset?: Record<string, { x: number; y: number }>;
    canvasWidthRatio?: Record<string, number>;
    safeAreas: Record<string, { margin: number }>;
    mockups: Record<string, string>;
    surfaceLabels: Record<string, string>;
  };
  isActive: boolean;
  variants: Array<{
    id: string;
    size: string;
    color: string;
    colorHex: string;
    outlineHex: string;
    additionalPrice: number;
    stockCount: number;
    isActive: boolean;
    isDefault: boolean;
  }>;
  images: Array<{
    id: string;
    url: string;
    isPrimary: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface DesignDetail {
  id: string;
  name: string;
  description: string | null;
  canvasData: Record<string, unknown>;
  previewUrl: string | null;
  status: string;
  product: DesignDetailProduct;
  createdAt: string;
  updatedAt: string;
}

interface CreateDesignDto {
  productId: string;
  name?: string;
  description?: string;
  canvasData?: Record<string, unknown>;
  status?: string;
  totalPrice?: number;
  surfacesUsed?: string[];
  priceBreakdown?: PriceBreakdown;
  surfaceExports?: Record<string, string>;
}

interface UpdateDesignDto {
  name?: string;
  description?: string;
  canvasData?: Record<string, unknown>;
  status?: string;
  totalPrice?: number;
  surfacesUsed?: string[];
  priceBreakdown?: PriceBreakdown;
  surfaceExports?: Record<string, string>;
  previewUrl?: string;
}

export const designsApi = {
  async list(): Promise<DesignListItem[]> {
    return apiJson<DesignListItem[]>('/designs');
  },

  async get(id: string): Promise<DesignDetail> {
    return apiJson<DesignDetail>(`/designs/${id}`);
  },

  async create(dto: CreateDesignDto): Promise<{ id: string }> {
    return apiJson<{ id: string }>('/designs', { method: 'POST', body: dto });
  },

  async update(id: string, dto: UpdateDesignDto): Promise<DesignDetail> {
    return apiJson<DesignDetail>(`/designs/${id}`, { method: 'PUT', body: dto });
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/designs/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Delete failed');
  },

  async uploadSurfaceExport(
    file: File,
    designId: string,
  ): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('designId', designId);
    return apiJson<{ url: string }>('/designs/upload-surface-export', {
      method: 'POST',
      body: fd,
    });
  },
};
